import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BsCopy } from 'react-icons/bs';
import VerticalContentCard from '../Category/VerticalContentCard';
import BookCard from '../library/BookCard';

// ── Regex و توابع کمکی (دقیقاً از AIPage اصلی) ──
const CARD_LINK_REGEX = /(?:🎓|📚|🎙️|-)?\s*\[([^\]]+)\]\((\/(?:products|books|media)\/[^)]+)\)/g;
const PAGE_LINK_REGEX = /\[([^\]]+)\]\((\/[^)]+)\)/g;

function stripHtml(str) {
  return str.replace(/<[^>]*>/g, '');
}

function parseMessageContent(rawContent) {
  const content = stripHtml(rawContent);
  const blocks = [];
  const lines = content.split('\n');
  let textBuffer = [];
  let currentCards = [];

  const flushText = () => {
    const txt = textBuffer.join('\n').trim();
    if (txt) blocks.push({ type: 'text', value: txt });
    textBuffer = [];
  };
  const flushCards = () => {
    if (currentCards.length) {
      blocks.push({ type: 'cards', items: currentCards });
      currentCards = [];
    }
  };

  for (const line of lines) {
    CARD_LINK_REGEX.lastIndex = 0;
    const cardMatch = CARD_LINK_REGEX.exec(line);
    if (cardMatch) {
      flushText();
      currentCards.push({
        title: cardMatch[1].trim(),
        url: cardMatch[2].trim(),
        itemType: cardMatch[2].startsWith('/books/') ? 'book' : 'product',
      });
      continue;
    }
    flushCards();
    PAGE_LINK_REGEX.lastIndex = 0;
    const pageMatch = PAGE_LINK_REGEX.exec(line);
    if (pageMatch) {
      const before = line.slice(0, pageMatch.index).trim();
      if (before) textBuffer.push(before);
      flushText();
      blocks.push({ type: 'link', title: pageMatch[1].trim(), url: pageMatch[2].trim() });
      const after = line.slice(pageMatch.index + pageMatch[0].length).trim();
      if (after) textBuffer.push(after);
    } else {
      textBuffer.push(line);
    }
  }
  flushText();
  flushCards();
  return blocks;
}

function extractSlug(url) {
  const parts = url.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
}

// ── هوک دریافت داده کارت‌ها ──
function useCardData(items, token) {
  const [cardData, setCardData] = useState({});
  const [loading, setLoading] = useState(false);
  const cardDataRef = useRef(cardData);
  useEffect(() => { cardDataRef.current = cardData; }, [cardData]);

  const itemsKey = items ? items.map(i => i.url).join(',') : '';

  useEffect(() => {
    if (!items || items.length === 0 || !token) return;

    const missing = items.filter(
      item => !cardDataRef.current[`${item.itemType}:${item.url}`]
    );
    if (missing.length === 0) return;

    setLoading(true);
    const payload = missing.map(item => ({
      type: item.itemType === 'book' ? 'book' : 'product',
      slug: extractSlug(item.url),
      title: item.title,
    }));

    fetch('https://farzaneh.app/api/v1/ai/cards/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ items: payload }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !data.results) return;
        const map = {};
        missing.forEach((item, idx) => {
          const result = data.results[idx];
          if (result) map[`${item.itemType}:${item.url}`] = result;
        });
        setCardData(prev => ({ ...prev, ...map }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [itemsKey, token]);

  return { cardData, loading };
}

// ── بلوک کارت‌ها (استفاده داخلی) ──
const CardsBlock = ({ items, token }) => {
  const { cardData } = useCardData(items, token);

  return (
    <div className="flex flex-col gap-3 my-3 w-full">
      {items.map((item, idx) => {
        const key = `${item.itemType}:${item.url}`;
        const data = cardData[key] || {
          type: item.itemType,
          slug: extractSlug(item.url),
          title: item.title,
          cover_url: null,
          url: item.url,
        };

        if (item.itemType === 'book') {
          return (
            <div key={idx} className="w-full">
              <BookCard
                book={{
                  slug: data.slug || extractSlug(item.url),
                  title: data.title || item.title,
                  cover_image_url: data.cover_url || '',
                  author: data.author ? { name: data.author } : null,
                  translator: data.translator ? { name: data.translator } : null,
                  publisher: data.publisher ? { title: data.publisher } : null,
                  tags: data.tags || [],
                }}
              />
            </div>
          );
        }

        return (
          <div key={idx} className="w-full">
            <VerticalContentCard
              data={{
                title: data.title || item.title,
                cover_url: data.cover_url || null,
                price_display: data.price_display || '',
                is_free: data.is_free || false,
                description: data.description || '',
                slug: data.slug || extractSlug(item.url),
                product_url: data.url || item.url,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

// ── لینک قابل کلیک ──
const PageLink = ({ title, url }) => (
  <Link
    to={url}
    className="inline-flex items-center gap-1 text-blue-600 underline underline-offset-2 hover:text-blue-800 transition-colors text-sm"
  >
    {title}
  </Link>
);

// ── کامپوننت اصلی MessageBubble ──
export default function MessageBubble({ message, getToken }) {
  const isUser = message.role === 'user';
  const blocks = isUser
    ? [{ type: 'text', value: message.content }]
    : parseMessageContent(message.content);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={[
          'w-fit max-w-[88%] rounded-2xl px-4 py-3 shadow-lg',
          isUser
            ? 'bg-white/40 backdrop-blur-lg border border-white/50 text-gray-900 rounded-br-sm'
            : 'bg-white/30 backdrop-blur-lg border border-blue-200/50 neon-glow text-gray-800 rounded-bl-sm'
        ].join(' ')}
      >
        <div className="flex justify-between items-center mb-1.5 text-[11px] opacity-60 gap-4">
          <span className="font-semibold">{isUser ? 'شما' : 'فرزانه'}</span>
          {!isUser && message.content && (
            <button
              onClick={() => copyToClipboard(message.content)}
              className="hover:opacity-100"
              aria-label="کپی"
            >
              <BsCopy size={12} />
            </button>
          )}
        </div>

        {/* مراحل تفکر (اگر موجود باشد) */}
        {!isUser && message.thinkingSteps && message.thinkingSteps.length > 0 && (
          <div className="flex flex-col gap-1.5 mb-2 text-xs text-gray-700">
            {message.thinkingSteps.map((s, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full border border-white/30 shadow-sm">
                <span className="text-base">{s.emoji || '🧠'}</span>
                <span>{s.content}</span>
              </div>
            ))}
          </div>
        )}

        {/* کارت‌های ارسالی مستقیم از سرور (message.cards) */}
        {!isUser && message.cards && message.cards.length > 0 && (
          <CardsBlock
            items={message.cards.map(c => ({
              url: c.uri || c.url,
              title: c.title,
              itemType: c.type === 'book' ? 'book' : 'product',
            }))}
            token={getToken()}
          />
        )}

        {/* بلوک‌های متنی استخراج‌شده از مارک‌داون */}
        <div className="text-[12px] leading-7 font-light text-right">
          {blocks.map((block, i) => {
            if (block.type === 'cards') {
              return <CardsBlock key={i} items={block.items} token={getToken()} />;
            }
            if (block.type === 'link') {
              return <PageLink key={i} title={block.title} url={block.url} />;
            }
            return (
              <div
                key={i}
                className="whitespace-pre-wrap break-words"
                style={{ overflowWrap: 'anywhere' }}
              >
                {block.value}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}